// @givmo/org-console — the settings framework's two module seams and the Organization section's
// host-declared cards, as DATA (Org Machinery OM-17, design 4 + design 3).
//
// ══ THE PAGE OWNS THE FRAME; A MODULE OWNS ITS CONTENT ═════════════════════════════════════════
//
// The composed settings page (`org-settings-panel.tsx`) draws the section, its heading, the card
// chrome, the order and the permission gate. What a module brings arrives in one of two shapes:
//
//   * SEAM A — served DEFINITIONS. The server's registry describes a setting (`OrgSettingDefinition`
//     in `wire-types.ts`: key, module, kind, choices, default, label, description, the ONE permission
//     string the write needs, order) and the page renders it as a knob card. Nothing is declared on
//     the web for seam A: a definition is served iff the class carries the module, the organization
//     has it ON and the caller holds its string.
//   * SEAM B — a module-OWNED panel (`ModuleSettings` below). For settings a definitions renderer
//     cannot carry: a builder, a form with dependent fields, a preview, a store outside the org
//     plane. Declared beside the module's `ModuleNav` in the host's `consoleDeclarations()`.
//
// ══ THE GATE IS A STRING, NEVER A TIER (the build law) ═════════════════════════════════════════
//
// `requires` is the ONE permission string the panel's WRITE needs — the same string the service's
// decorator names, declared once here and enforced once there. The page mounts a panel iff that
// string is in the caller's SETTLED effective set and the module is the page's origin; absence
// otherwise, never a disabled control. A not-reported set (`null`) mounts nothing.
//
// ══ WHAT A PANEL OWES ══════════════════════════════════════════════════════════════════════════
//
// It owns its reads and writes through ITS transport (this package has no write path to a module's
// store), its own save semantics under one law — an explicit Edit → Save, dirty-gated, for a
// multi-field form; autosave with rollback for a single choice — and it reports its dirty state
// through `registerDirty` so leaving the page prompts. A 403 on save is surfaced inline. Its copy
// makes no promise and its DECLARING package runs the shared vocabulary scanner over its own `src` —
// the repo's copy rails are app-scoped and this package scans only its own sources. It never draws
// a section heading (the page's) and never a second card frame.

import type { ComponentType } from 'react';
import type { ModuleStanding } from './module-verdict';
import type { OrgConsoleConfig } from './org-console-config';

/** What a seam-B panel is handed — the props contract (design 4). */
export interface SettingsPanelProps {
  /** The organization this page is scoped to. */
  orgRef: string;
  /** The module the panel belongs to — the page's origin. */
  module: string;
  /** The caller's SETTLED effective permission set (never `null` here: nothing mounts on a guess). */
  permissions: readonly string[];
  /** The caller's standing in this module, or `null` when the standing read did not report it. */
  standing: ModuleStanding | null;
  /** The console's config — the vocab, the org ref, the host's navigation hook. */
  config: OrgConsoleConfig;
  /** Follow an in-app destination with the host's router instead of a document load. */
  onNavigate?: (href: string) => void;
  /**
   * Report the panel's dirty state to the page's unsaved-changes guard. Call it whenever the
   * state changes; the page prompts before leaving while ANY mounted panel is dirty.
   */
  registerDirty: (dirty: boolean) => void;
}

/** One module-owned panel, slotted into the module's section on the composed page. */
export interface ModuleSettingsPanel {
  /** Stable, unique within the module — the slot's testid derives from it. */
  id: string;
  /** Display order within the section (ascending). Declared panels follow served definitions. */
  order: number;
  /** The ONE permission string the panel's write requires. An empty string requires nothing beyond being an admin here. */
  requires: string;
  component: ComponentType<SettingsPanelProps>;
}

/** A module's declared settings, registered beside its `ModuleNav` in the host's declarations. */
export interface ModuleSettings {
  slug: string;
  panels: readonly ModuleSettingsPanel[];
}

/** What an Organization-section card is handed. */
export interface OrganizationSettingsCardProps {
  orgRef: string;
  config: OrgConsoleConfig;
  /** The caller's SETTLED effective permission set. */
  permissions: readonly string[];
  /** The caller's capabilities off the standing read, or `null` when not reported. */
  capabilities: readonly string[] | null;
  onNavigate?: (href: string) => void;
}

/**
 * An org-level setting or act the HOST declares for the Organization section (design 3): class
 * content — the class string the host names — or org machinery gated on a capability. Knobs first,
 * then the GRAVE acts (`grave: true`), each by `order`; the *Deactivate organization* card is
 * always the last card and is never declared here.
 */
export type OrganizationSettingsCard = {
  /** Stable, unique across the host's cards — the slot's testid derives from it. */
  id: string;
  /** Display order within its group (ascending). */
  order: number;
  /** A grave or binding org ACT — rendered after every knob, before Deactivate. */
  grave?: true;
  component: ComponentType<OrganizationSettingsCardProps>;
} & (
  | {
      /** The class string the card's write requires, off the standing read's effective set. */
      requires: string;
      capability?: never;
    }
  | {
      /** The capability the card's write requires, off the standing read's capabilities. */
      capability: string;
      requires?: never;
    }
);

/**
 * The organization's own setting group (seam A's `ORG_SETTING_GROUP` on the backend): a served
 * definition whose `module` is this is about the organization AS SUCH, so it renders in the
 * Organization section as a knob, never in a module's section. Not a module slug.
 */
export const ORG_SETTING_GROUP = 'org';

/** Whether a SETTLED effective set admits a string. An empty string admits any admin here. */
export function holdsRequirement(permissions: readonly string[], requires: string): boolean {
  if (requires.trim().length === 0) return true;
  return permissions.includes(requires);
}
